import styled, { css, keyframes } from 'styled-components';

import { media } from '../../utils/mediaQueries';

/* ==========================================================================
   Wejście ekranu — jeden ruch dla wszystkich bloków.

   Elementy pojawiają się po kolei, od nagłówka po stopkę: oko dostaje kolejność
   czytania, a ekran nie „wskakuje" całością. Animacja jest krótka i przesuwa
   o kilkanaście pikseli, więc nie opóźnia pracy nikomu, kto zna ten formularz na pamięć.
   ========================================================================== */
const authRise = keyframes`
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

/** Wejście z opóźnieniem. Bez `both` element mignąłby w pełnej widoczności przed startem. */
const rise = (delay: number) => css`
  animation: ${authRise} 560ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms both;
`;

/* ==========================================================================
   Akcent auth — ten sam grafit co aktywny element listy w sidebarze
   (Sidebar.styled.ts: sbActiveBg). W trybie ciemnym akcent jest odwrócony,
   bo grafit zlałby się z ciemnym tłem formularza.
   ========================================================================== */
export const authAccentBg = (mode: string) => (mode === 'dark'
  ? '#f9fafb'
  : 'linear-gradient(180deg, #1f2937 0%, #111827 100%)');
export const authAccentBgHover = (mode: string) => (mode === 'dark'
  ? '#ffffff'
  : 'linear-gradient(180deg, #111827 0%, #030712 100%)');
export const authAccentOn = (mode: string) => (mode === 'dark' ? '#111827' : '#ffffff');
export const authAccentText = (mode: string) => (mode === 'dark' ? '#e5e7eb' : '#111827');
export const authAccentBorder = (mode: string) => (mode === 'dark' ? '#9ca3af' : '#1f2937');
export const authAccentRing = (mode: string) => (mode === 'dark'
  ? 'rgba(255, 255, 255, 0.15)'
  : 'rgba(17, 24, 39, 0.15)');
export const authAccentShadow = (mode: string) => (mode === 'dark'
  ? 'rgba(255, 255, 255, 0.2)'
  : 'rgba(17, 24, 39, 0.3)');

// ============================================
// Main Container
// ============================================

export const AuthContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#0f0f0f' : '#f8f9fa')};
`;

// ============================================
// Left Side - Form Section
// ============================================

export const FormContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 40px;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#1a1a1a' : '#ffffff')};

  ${media.desktop} {
    max-width: 50%;
  }

  ${media.tabletDown} {
    max-width: 100%;
  }
`;

export const FormWrapper = styled.div`
  width: 100%;
  max-width: 400px;

  > *:nth-child(1) { ${rise(60)} }
  > *:nth-child(2) { ${rise(120)} }
  > *:nth-child(3) { ${rise(170)} }
  > *:nth-child(n + 4) { ${rise(220)} }

  ${media.reducedMotion} {
    > * {
      animation: none;
    }
  }
`;

export const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 40px;

  svg {
    height: 32px;
    width: auto;
  }
`;

export const LogoText = styled.span`
  font-size: 24px;
  font-weight: 400;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#ffffff' : '#1a1a1a')};
`;

export const WelcomeTitle = styled.h1`
  font-size: 28px;
  font-weight: 500;
  letter-spacing: -0.02em;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#ffffff' : '#1a1a1a')};
  margin: 0 0 8px 0;
`;

export const WelcomeSubtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#a1a1a1' : '#6b7280')};
  margin: 0 0 32px 0;
  line-height: 1.5;
`;

export const AuthForm = styled.form`
  width: 100%;

  /* Kontynuacja rytmu z kolumny formularza: najpierw nagłówek, potem pola, na końcu przycisk. */
  > *:nth-child(1) { ${rise(260)} }
  > *:nth-child(2) { ${rise(320)} }
  > *:nth-child(3) { ${rise(380)} }
  > *:nth-child(n + 4) { ${rise(430)} }

  ${media.reducedMotion} {
    > * {
      animation: none;
    }
  }
`;

export const InputGroup = styled.div`
  margin-bottom: 20px;
`;

export const InputLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#e5e5e5' : '#374151')};
  margin-bottom: 8px;
`;

export const InputWrapper = styled.div`
  position: relative;

  /* Ikona ciemnieje razem z ramką, więc aktywne pole widać jednym rzutem oka. */
  &:focus-within > div:first-child {
    color: ${({ theme }) => authAccentBorder(theme.mode)};
  }
`;

export const InputIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => (theme.mode === 'dark' ? '#6b7280' : '#9ca3af')};
  display: flex;
  align-items: center;

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const StyledInput = styled.input`
  width: 100%;
  height: 48px;
  padding: 0 44px 0 44px;
  font-size: 14px;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};
  border-radius: 10px;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#111111' : '#ffffff')};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#ffffff' : '#1a1a1a')};
  caret-color: ${({ theme }) => authAccentBorder(theme.mode)};
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;

  &:hover:not(:focus) {
    border-color: ${({ theme }) => (theme.mode === 'dark' ? '#4b5563' : '#d1d5db')};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => authAccentBorder(theme.mode)};
    box-shadow: 0 0 0 3px ${({ theme }) => authAccentRing(theme.mode)};
  }

  &::placeholder {
    color: ${({ theme }) => (theme.mode === 'dark' ? '#6b7280' : '#9ca3af')};
  }

  /* Podpowiedź menedżera haseł ma tło przeglądarki; wyrównujemy je do pola. */
  &:-webkit-autofill,
  &:-webkit-autofill:focus {
    -webkit-text-fill-color: ${({ theme }) => (theme.mode === 'dark' ? '#ffffff' : '#1a1a1a')};
    box-shadow: 0 0 0 100px ${({ theme }) => (theme.mode === 'dark' ? '#111111' : '#ffffff')} inset;
  }
`;

export const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#6b7280' : '#9ca3af')};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;

  &:hover {
    color: ${({ theme }) => (theme.mode === 'dark' ? '#e5e5e5' : '#374151')};
  }

  &:focus-visible {
    outline: none;
    border-radius: 6px;
    box-shadow: 0 0 0 3px ${({ theme }) => authAccentRing(theme.mode)};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const RememberRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

export const CheckboxWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#a1a1a1' : '#6b7280')};

  input[type="checkbox"] {
    appearance: none;
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    min-width: 16px;
    border-radius: 4px;
    border: 1.5px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#d1d5db')};
    cursor: pointer;
    position: relative;
    transition: all 0.15s ease;
    background: transparent;

    &:checked {
      background: ${({ theme }) => authAccentBg(theme.mode)};
      border-color: ${({ theme }) => authAccentBorder(theme.mode)};

      &::after {
        content: '';
        position: absolute;
        left: 4px;
        top: 1px;
        width: 5px;
        height: 9px;
        border: solid ${({ theme }) => authAccentOn(theme.mode)};
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
      }
    }
  }
`;

export const ForgotPasswordLink = styled.a`
  font-size: 14px;
  color: ${({ theme }) => authAccentText(theme.mode)};
  text-decoration: none;
  font-weight: 500;
  border-radius: 4px;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => authAccentRing(theme.mode)};
  }
`;

export const SubmitButton = styled.button`
  width: 100%;
  height: 50px;
  padding: 0 12px;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: ${({ theme }) => authAccentOn(theme.mode)};
  background: ${({ theme }) => authAccentBg(theme.mode)};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: ${({ theme }) => authAccentBgHover(theme.mode)};
    transform: translateY(-1px);
    box-shadow: 0 8px 20px ${({ theme }) => authAccentShadow(theme.mode)};
  }

  /* Wciśnięcie wraca do linii bazowej: przycisk odpowiada na kliknięcie od razu,
     jeszcze zanim serwer zdąży cokolwiek odpowiedzieć. */
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: none;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => authAccentRing(theme.mode)};
  }

  /* Wygląd bez zmian — disabled sygnalizuje tylko kursor, nie wyszarzenie. */
  &:disabled {
    cursor: not-allowed;
  }
`;

export const Divider = styled.div`
  //display: flex;
  display: none;
  align-items: center;
  margin: 32px 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};
  }

  span {
    padding: 0 16px;
    font-size: 12px;
    color: ${({ theme }) => (theme.mode === 'dark' ? '#6b7280' : '#9ca3af')};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

export const SocialButton = styled.button`
  display: none;
  //display: flex;
  width: 100%;
  padding: 16px;
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#e5e5e5' : '#374151')};
  background: transparent;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;

  &:hover {
    background: ${({ theme }) => (theme.mode === 'dark' ? '#262626' : '#f9fafb')};
    border-color: ${({ theme }) => (theme.mode === 'dark' ? '#4b5563' : '#d1d5db')};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const LegalDisclaimer = styled.p`
  text-align: center;
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#8a8a8a' : '#9ca3af')};
  margin-top: 16px;
  max-width: 360px;
  margin-left: auto;
  margin-right: auto;

  a {
    color: ${({ theme }) => (theme.mode === 'dark' ? '#a1a1a1' : '#6b7280')};
    text-decoration: underline;

    &:hover {
      color: ${({ theme }) => authAccentText(theme.mode)};
    }
  }
`;

export const SignUpLink = styled.p`
  display: none;
  text-align: center;
  font-size: 14px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#a1a1a1' : '#6b7280')};
  margin-top: 24px;

  a {
    color: ${({ theme }) => authAccentText(theme.mode)};
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;

// ============================================
// Right Side - Marketing Section
// ============================================

/*
 * Panel po prawej jest BIAŁY niezależnie od motywu. Nagranie telefonu ma białe tło, więc
 * w trybie ciemnym prostokąt wideo świeciłby jak wycięty z innej strony. Przy okazji
 * czarne teksty tej sekcji przestają znikać na ciemnym tle.
 */
export const MarketingSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 72px 64px;
  position: relative;
  overflow: hidden;
  /* Ta sama biel co w kolumnie formularza, a podział niesie włos linii. Panel jest
     biały niezależnie od motywu, bo teksty tej sekcji mają wpisany czarny kolor. */
  background: #ffffff;
  border-left: 1px solid #eef0f3;

  ${media.tabletDown} {
    display: none;
  }
`;

export const MarketingContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 520px;
  text-align: left;

  > *:nth-child(1) { ${rise(120)} }
  > *:nth-child(2) { ${rise(200)} }
  > *:nth-child(3) { ${rise(280)} }
  > *:nth-child(n + 4) { ${rise(360)} }

  ${media.reducedMotion} {
    > * {
      animation: none;
    }
  }
`;

/* Stopień pisma i grubość jak w „Witaj ponownie!" (`WelcomeTitle`): obie kolumny mają
   nagłówek tej samej rangi, więc różne rozmiary robiły z prawej strony baner.
   Interlinia luźniejsza, bo ten nagłówek łamie się na dwie linijki. */
export const MarketingTitle = styled.h2`
  font-size: 28px;
  font-weight: 500;
  letter-spacing: -0.02em;
  color: #0b1220;
  margin-bottom: 18px;
  line-height: 1.35;
  text-align: left;
`;

export const MarketingDescription = styled.p`
  font-size: 16px;
  color: #4b5563;
  line-height: 1.65;
  margin-bottom: 40px;
  text-align: left;
`;

export const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: left;
`;

export const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: rgba(0, 0, 0, 0.9);
  font-size: 14px;

  svg {
    width: 20px;
    height: 20px;
    color: #000000;
  }
`;

export const PartnersSection = styled.div`
  margin-top: 60px;
  padding-top: 40px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
`;

export const PartnersTitle = styled.p`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 24px;
`;

/*
 * Pasek partnerów zawija się sam, zamiast trzymać sztywne cztery kolumny: marek jest
 * dziewięć i po każdym dodaniu kolejnej siatka zostawiała pustą, wyrwaną kratkę.
 */
export const PartnersGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 24px 36px;

  img {
    height: 34px;
    max-width: 140px;
    object-fit: contain;
  }

  /* Kwadratowe znaki (ibis) obok logotypów słownych: niższy kadr wyrównuje ciężar. */
  img.mark {
    height: 30px;
    max-width: 30px;
  }
`;

// ============================================
// Footer Components
// ============================================

export const AuthFooter = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px 24px;
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(26, 26, 26, 0.95)' : 'rgba(255, 255, 255, 0.95)')};
  backdrop-filter: blur(10px);
  border-top: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};
  z-index: 100;
`;

export const LanguageLabel = styled.span`
  font-size: 13px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  font-weight: 500;
`;

export const LanguageSelect = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  font-size: 13px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#e5e5e5' : '#374151')};
  background: transparent;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#d1d5db')};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => (theme.mode === 'dark' ? '#262626' : '#f9fafb')};
    border-color: ${({ theme }) => (theme.mode === 'dark' ? '#4b5563' : '#9ca3af')};
  }

  svg {
    opacity: 0.7;
  }
`;

export const FooterDivider = styled.div`
  width: 1px;
  height: 20px;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};
`;

// ============================================
// Legacy Components (dla InvitePin, SetPassword, UserInvite)
// ============================================

export const MainContainer = styled.div`
  display: flex;
  min-height: 100vh;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#0f0f0f' : '#f8f9fa')};
  padding: 40px 20px;
`;

export const AuthCard = styled.div<{ $centered?: boolean }>`
  display: flex;
  width: 100%;
  max-width: ${({ $centered }) => ($centered ? '500px' : '900px')};
  background: ${({ theme }) => (theme.mode === 'dark' ? '#1a1a1a' : '#ffffff')};
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  overflow: hidden;

  ${({ $centered }) => $centered && `
    flex-direction: column;
  `}

  ${media.tabletDown} {
    flex-direction: column;
    max-width: 100%;
  }
`;

export const FormSection = styled.div`
  flex: 1;
  padding: 48px;

  ${media.tabletDown} {
    padding: 32px 24px;
  }
`;

export const CoverSection = styled.div<{ $bg?: string }>`
  flex: 1;
  background-image: ${({ $bg }) => ($bg ? `url(${$bg})` : 'none')};
  background-size: cover;
  background-position: center;
  background-color: ${({ theme }) => (theme.mode === 'dark' ? '#1a4d4d' : '#1a4d4d')};
  min-height: 400px;

  ${media.tabletDown} {
    display: none;
  }
`;

export const LoginHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 32px;

  svg {
    height: 32px;
    width: auto;
  }
`;

export const Title = styled.h1`
  font-size: 28px;
  font-weight: 400;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#ffffff' : '#1a1a1a')};
  margin: 0 0 12px 0;
`;

export const Content = styled.p`
  font-size: 14px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#a1a1a1' : '#6b7280')};
  margin: 0 0 32px 0;
  line-height: 1.6;
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

export const PrimaryButton = styled.button<{ isLoading?: boolean }>`
  width: 100%;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 300;
  color: ${({ theme }) => authAccentOn(theme.mode)};
  background: ${({ theme }) => authAccentBg(theme.mode)};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: ${({ theme }) => authAccentBgHover(theme.mode)};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${({ theme }) => authAccentShadow(theme.mode)};
  }

  /* Wygląd bez zmian — disabled sygnalizuje tylko kursor, nie wyszarzenie. */
  &:disabled {
    cursor: not-allowed;
  }
`;

export const BackLink = styled.div`
  text-align: center;
  margin-top: 24px;

  a {
    font-size: 14px;
    color: ${({ theme }) => authAccentText(theme.mode)};
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;
